// Tabs - wires [role="tab"] triggers to .tab panes, manages aria attributes,
// hash deep-linking ([data-tab-load]) and nav state. Behavioral port of v1
// modules/Tab.js.

import { getSiblings } from '../utils';
import { TOGGLE_EVT } from './toggle';

export interface TabConfig {
  activeClass: string;
  navContainerSelector: string;
  navItemSelector: string;
  tabPanesContainerSelector: string;
  tabPaneSelector: string;
  scrollDelay: number;
}

const defaultConfig: TabConfig = {
  activeClass: 'is-active',
  navContainerSelector: '[data-tabs-container]',
  navItemSelector: '[data-tabs-item]',
  tabPanesContainerSelector: '.tabs',
  tabPaneSelector: '.tab',
  scrollDelay: 300,
};

export default class Tab {
  selector: string;

  config: TabConfig;

  triggers: Element[] = [];

  tabs: Element[] = [];

  constructor(
    selector = '[role="tab"]:not(.CybotCookiebotDialogNavItemLink)',
    config: Partial<TabConfig> = {},
  ) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  destroy(): void {
    this.triggers.forEach(trigger => {
      trigger.removeEventListener('click', this._onClick);
    });
    this.tabs.forEach(tab => {
      tab.removeEventListener(TOGGLE_EVT, this._handleToggle);
    });
    this.triggers = [];
    this.tabs = [];
  }

  update(): void {
    this.destroy();
    this._init();
  }

  private _init(): void {
    this.triggers.push(...document.querySelectorAll(this.selector));
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', this._onClick);
    });

    this.tabs.push(...document.querySelectorAll(this.config.tabPaneSelector));
    this.tabs.forEach(tab => {
      tab.addEventListener(TOGGLE_EVT, this._handleToggle);
    });

    this._assignTriggerAttributes();
    this._assignTabAttributes();
    this._openTabOnLoad();
    this._activateFirstTab();
  }

  private _assignTriggerAttributes(): void {
    this.triggers.forEach(trigger => {
      const isActive = trigger.classList.contains(this.config.activeClass);

      const href = trigger.getAttribute('href') ?? '';
      let id: string;

      if (href.startsWith('#')) {
        id = href.slice(1);
      } else {
        const url = new URL(href, window.location.origin);
        id = url.hash.slice(1);
      }

      if (!id) {
        id = Math.floor(Math.random() * Date.now()).toString(16);
      }

      trigger.setAttribute('aria-selected', String(isActive));
      trigger.setAttribute('aria-controls', id);
      trigger.setAttribute('id', `${id}-label`);
    });

    document
      .querySelectorAll(this.config.navContainerSelector)
      .forEach(container => {
        container.setAttribute('role', 'tablist');
      });
  }

  private _assignTabAttributes(): void {
    this.tabs.forEach(tab => {
      const isActive = tab.classList.contains(this.config.activeClass);
      tab.setAttribute('role', 'tabpanel');
      tab.setAttribute('aria-hidden', String(!isActive));
      tab.setAttribute('aria-labelledby', `${tab.getAttribute('id')}-label`);
    });
  }

  /** Ensure each pane container and nav container has exactly one active item. */
  private _activateFirstTab(): void {
    document
      .querySelectorAll(this.config.tabPanesContainerSelector)
      .forEach(container => {
        const tabs = container.querySelectorAll(this.config.tabPaneSelector);
        const hasActive = [...tabs].some(tab =>
          tab.classList.contains(this.config.activeClass));

        if (!hasActive && tabs.length) {
          tabs.item(0).classList.add(this.config.activeClass);
        }
      });

    document
      .querySelectorAll(this.config.navContainerSelector)
      .forEach(container => {
        const navItems = container.querySelectorAll(this.config.navItemSelector);
        const hasActive = [...navItems].some(item =>
          item.classList.contains(this.config.activeClass));

        if (!hasActive && navItems.length) {
          navItems.item(0).classList.add(this.config.activeClass);
        }
      });
  }

  private _openTabOnLoad(): void {
    const hash = window.location.hash.slice(1);

    if (!hash) {
      return;
    }

    for (const tab of this.tabs) {
      if (tab.getAttribute('id') === hash) {
        const tabContainer = tab.closest(this.config.tabPanesContainerSelector);

        if (tabContainer && tabContainer.hasAttribute('data-tab-load')) {
          this.toggleTab(tab);
          this._scrollToTab(tab);

          break;
        }
      }
    }
  }

  private _scrollToTab(el: Element): void {
    const trigger = this._findTrigger(el);
    const triggerContainer = trigger?.closest('[role=tablist]');

    if (!triggerContainer) {
      return;
    }

    setTimeout(() => {
      // Skip when the user has already scrolled somewhere.
      if (window.scrollY > 0) {
        return;
      }

      triggerContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, this.config.scrollDelay);
  }

  private _onClick = (e: Event): void => {
    const href = (e.currentTarget as Element).getAttribute('href');

    if (!href) {
      return;
    }

    const url = new URL(href, window.location.origin);
    const { hash } = url;

    if (!hash) {
      return;
    }

    const tab = document.querySelector(hash);

    this.toggleTab(tab);
    e.preventDefault();
  };

  private _handleToggle = (e: Event): void => {
    this.toggleTab(e.currentTarget as Element);
  };

  toggleTab(el: Element | string | null): boolean {
    const tab = el instanceof Element ? el : el ? document.querySelector(el) : null;
    if (!tab) {
      return false;
    }
    const container = tab.closest(this.config.tabPanesContainerSelector);
    if (!container) {
      return false;
    }
    getSiblings(tab).forEach(sibling => {
      sibling.classList.remove(this.config.activeClass);
      sibling.setAttribute('aria-hidden', 'true');
    });
    tab.classList.add(this.config.activeClass);
    tab.setAttribute('aria-hidden', 'false');
    this._toggleTabNav(tab);
    if (tab.parentElement?.hasAttribute('data-tab-load')) {
      const currentUrl = new URL(window.location.href);
      currentUrl.hash = `#${tab.getAttribute('id')}`;
      window.history.replaceState(undefined, '', currentUrl.toString());
    }
    return true;
  }

  private _toggleTabNav(el: Element): void {
    const trigger = this._findTrigger(el);
    if (!trigger) {
      return;
    }

    const triggerContainer = trigger.closest(this.config.navContainerSelector);
    if (!triggerContainer) {
      return;
    }

    // The active trigger sits either inside the active nav item or on it.
    const currentItem =
      triggerContainer.querySelector(
        `${this.config.navItemSelector}.${this.config.activeClass} ${this.selector}`,
      )
      || triggerContainer.querySelector(
        `${this.config.navItemSelector}.${this.config.activeClass}${this.selector}`,
      );
    if (trigger === currentItem) {
      return;
    }

    if (currentItem) {
      currentItem
        .closest(this.config.navItemSelector)
        ?.classList.remove(this.config.activeClass);
      currentItem.setAttribute('aria-selected', 'false');
    }

    trigger
      .closest(this.config.navItemSelector)
      ?.classList.add(this.config.activeClass);
    trigger.setAttribute('aria-selected', 'true');
  }

  private _findTrigger(tab: Element): Element | null {
    const tabId = tab.getAttribute('id');

    return document.querySelector(
      `${this.selector}[href="#${tabId}"], ${this.selector}[href$="#${tabId}"]`,
    );
  }
}
